import type { SampleSlice } from '@/hooks/useSampleUpload'

/**
 * Thin numbered strip underneath the waveform. Gives the user an
 * explicit "which slice is this?" reference — indispensable when
 * the waveform is zoomed in and a slice fills more than the viewport.
 *
 * Positioned below the waveform (not above) so fingers scrubbing /
 * zooming on a touchscreen don't cover the sample visualisation.
 *
 * Scrolls in lockstep with the waveform canvas because it shares
 * the same zoomed inner-wrapper width and lives inside the same
 * horizontally-scrollable container.
 *
 * Optional: clicking a number jumps the container scroll so that
 * slice is visible — effectively making the ribbon a mini-map.
 */

interface SliceIndexRibbonProps {
  slices: SampleSlice[]
  durationSec: number
  /** Pixel width of the ribbon — should match the (zoomed)
   *  waveform canvas width exactly. */
  width: number
  /** Click a number → jump to that slice. Wire to the container's
   *  scrollTo so zoomed viewports centre on the picked slice. */
  onJumpTo?: (index: number) => void
}

export function SliceIndexRibbon({
  slices,
  durationSec,
  width,
  onJumpTo,
}: SliceIndexRibbonProps) {
  if (!slices.length || durationSec <= 0) return null
  return (
    <div
      className="relative bg-surface-sunken border-t border-brd-subtle"
      style={{ width, height: 18 }}
      aria-label="Slice index ribbon"
    >
      {slices.map((sl, i) => {
        // Centre each number over the midpoint of its slice so it
        // reads as "this is slice N" regardless of slice width.
        const midSec = (sl.startSec + sl.endSec) / 2
        const leftPx = (midSec / durationSec) * width
        const sliceWidthPx = Math.max(
          12,
          ((sl.endSec - sl.startSec) / durationSec) * width,
        )
        // Slice boundary tick — faint vertical line on the left edge
        // of every slice region. Keeps orientation even when the
        // slice is too narrow for a readable number.
        return (
          <div
            key={sl.id}
            className="absolute top-0 bottom-0"
            style={{
              left: (sl.startSec / durationSec) * width,
              width: sliceWidthPx,
            }}
          >
            <div
              aria-hidden
              className="absolute left-0 top-1 bottom-1 w-px bg-brd-subtle"
            />
            <button
              type="button"
              onClick={() => onJumpTo?.(i)}
              title={`Slice ${i + 1}${onJumpTo ? ' — click to jump' : ''}`}
              className="absolute top-0 h-full text-[9px] font-mono tabular-nums text-content-tertiary hover:text-accent leading-none flex items-center justify-center cursor-pointer"
              style={{
                // Position the number at the midpoint relative to
                // the ribbon itself, not the slice box, so numbers
                // stay horizontally centred under the slice.
                left: leftPx - (sl.startSec / durationSec) * width - 10,
                width: 20,
              }}
            >
              {i + 1}
            </button>
          </div>
        )
      })}
    </div>
  )
}
